<template>
  <div class="log-book">
    <div class="container">
      <div class="wrapper">
        <h1 class="title">
          <span>Zelda</span>
          <span>LogBook</span>
        </h1>
        <NavBar
          :navItems="navItems"
          :selectedItem="selectedItem"
          :onSelectItem="selectItem"
        />
        <SearchBar
          :initialQuery="searchQuery"
          @update:query="updateSearchQuery"
          @search="performSearch"
        />

        <div
          v-if="paginatedResults.length === 0 && !loading"
          class="no-results"
        >
          <h2>No Results (╯°□°)╯︵ ┻━┻</h2>
        </div>
        <div v-else class="results-container">
          <div class="results-header">
            <h2>Results</h2>
            <div class="divider"></div>
          </div>

          <transition name="fade" mode="out-in">
            <div
              v-if="!loading"
              :key="selectedItem + '_' + currentPage + '_' + searchKey"
              class="results-grid"
            >
              <ResultCard
                v-for="result in paginatedResults"
                :key="result.id"
                :id="result.id"
                :name="result.name"
                :description="result.description"
              />
            </div>
            <div v-else class="loading-placeholder">Loading...</div>
          </transition>

          <div class="pagination">
            <button
              @click="prevPage"
              :disabled="currentPage === 1 || loading"
              class="pagination-button"
            >
              Prev
            </button>
            <button
              @click="nextPage"
              :disabled="currentPage === totalPages || loading"
              class="pagination-button"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from 'vue';
import NavBar from '@/components/NavBar.vue';
import SearchBar from '@/components/SearchBar.vue';
import ResultCard from '@/components/ResultCard.vue';
import zeldaService from '@/services/zeldaService';

interface Result {
  id: string;
  name: string;
  description: string;
}

const searchQuery = ref('');
const searchKey = ref('');
const navItems = ['Game', 'Characters', 'Items', 'Monsters'];
const selectedItem = ref('Items');
const results = ref<Result[]>([]);
const currentPage = ref(1);
const pageSize = 6;
const loading = ref(false);

const totalPages = computed(() => Math.ceil(results.value.length / pageSize));

const paginatedResults = computed(() => {
  const start = (currentPage.value - 1) * pageSize;
  const end = start + pageSize;
  return results.value.slice(start, end);
});

const fetchResults = async () => {
  loading.value = true;
  results.value = await zeldaService.fetchResults(
    selectedItem.value,
    searchQuery.value,
  );
  loading.value = false;
};

const performSearch = () => {
  currentPage.value = 1;
  searchKey.value = searchQuery.value;
  fetchResults();
};

const updateSearchQuery = (newQuery: string) => {
  searchQuery.value = newQuery;
};

const selectItem = (item: string) => {
  selectedItem.value = item;
  currentPage.value = 1;
  searchQuery.value = '';
  searchKey.value = '';
  fetchResults();
};

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value -= 1;
    fetchResults();
  }
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value += 1;
    fetchResults();
  }
};

onMounted(() => {
  fetchResults();
});

watch([selectedItem], fetchResults);
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.log-book {
  padding: 2.5% 10%;
}

.container {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 75vh;
  background-color: $color-background;
  border-radius: 32px;
}

.wrapper {
  width: 80%;
}

.title {
  font-size: 80px;
  font-weight: 100;
  color: $color-foreground;
  letter-spacing: 0.02em;
  margin-bottom: 20px;

  span {
    display: flex;
    text-align: center;
    justify-content: center;
  }

  @media (max-width: 768px) {
    font-size: 10vw;
  }
}

.results-container {
  font-family: 'Montserrat';
  width: 100%;
  min-height: 500px;
  margin-top: 10rem;
  margin-bottom: 5rem;

  .results-header {
    display: flex;
    align-items: center;
    margin-bottom: 20px;

    h2 {
      font-size: 1.5em;
      color: $color-foreground;
      margin: 0;
    }

    .divider {
      flex: 1;
      height: 1px;
      background-color: $color-foreground;
      margin-left: 45px;
    }
  }

  .loading-placeholder {
    height: 250px;
    display: flex;
    justify-content: center;
    align-items: center;
    color: $color-foreground;
  }
}

.no-results {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  color: $color-foreground;
  font-size: 1.5em;
  font-weight: 500;
  text-align: center;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-in;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave {
  opacity: 1;
}

.results-grid {
  display: grid;
  height: 600px;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 768px) {
    height: auto;
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    height: auto;
    grid-template-columns: 1fr;
  }
}

.pagination {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  margin-bottom: 20px;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    align-items: center;
  }
}

.pagination-button {
  background-color: transparent;
  border: 2px solid $color-pag-button-border;
  color: $color-pag-button-border;
  padding: 8px 25px;
  border-radius: 100px;
  cursor: pointer;
  transition: background-color 0.3s;
  font-size: 1.1rem;

  @media (max-width: 768px) {
    margin: 10px 0;
    width: 100%;
  }

  &:hover {
    cursor: pointer;
    background-color: $color-pag-button-bg;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }
}
</style>
